import { useRef, useState, useEffect } from 'react';
import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect.js';

const COLOR_MODE_ATTRIBUTE = "data-experimental-ifl-color-mode";
const getAttributeValue = (ref, attributeToObserve) => {
  let value;
  if (ref.current) {
    const closestNodeWithAttribute = ref.current.closest(`[${attributeToObserve}]`);
    if (closestNodeWithAttribute) {
      value = closestNodeWithAttribute.attributes.getNamedItem(attributeToObserve)?.value;
    }
  }
  return value;
};

/**
 * Listens to changes to the color mode attribute on the closest ancestor to the ref element.
 *
 * @param ref the root element of the `IndeedThemeProvider`
 *
 * @returns the value (string) of the closest color mode attribute if found. If one isn't found (or if this hook hasn't
 * been able to check yet), the "light" default will be returned.
 */
function useColorModeAttribute(ref) {
  const initialRenderRef = useRef(true);
  const [colorMode, setColorMode] = useState("light");

  // Set initial value before paint
  useIsomorphicLayoutEffect(() => {
    if (!initialRenderRef.current) return;
    initialRenderRef.current = false;
    const attrValue = getAttributeValue(ref, COLOR_MODE_ATTRIBUTE);
    if (attrValue !== colorMode && (attrValue === "light" || attrValue === "dark")) {
      setColorMode(attrValue);
    }
  }, [ref]);

  // Attach observer for updates asynchronously
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const attrValue = getAttributeValue(ref, COLOR_MODE_ATTRIBUTE);
      if (attrValue !== colorMode && (attrValue === "light" || attrValue === "dark")) {
        setColorMode(attrValue);
      }
    });
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: [COLOR_MODE_ATTRIBUTE]
    });
    return () => observer.disconnect();
  }, [ref, colorMode]);
  return colorMode;
}

export { COLOR_MODE_ATTRIBUTE, useColorModeAttribute };
